import type { ButtonHTMLAttributes } from "react"

import { Slot } from "@radix-ui/react-slot"

import { tv, type VariantProps } from "@/lib/utils/tv"

const pill = tv({
  base: "hit-target inline-flex shrink-0 cursor-pointer items-center justify-center gap-150 rounded-full font-display whitespace-nowrap transition-colors select-none disabled:cursor-not-allowed disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:outline-none",
  variants: {
    tone: {
      neutral: "bg-surface-newgray text-fg hover:bg-surface-muted",
      brand: "bg-brand-subtle text-fg-brand hover:bg-brand-muted",
      palette: "bg-palette-soft text-palette-ink hover:bg-palette-card-tint",
      outline: "border-line-subtle bg-surface text-fg border hover:border-line-brand hover:text-fg-brand",
      event: "bg-event-gradient text-fg-inverted font-extrabold",
    },
    size: {
      xs: "h-7.5 px-300 text-body-sm font-semibold",
      sm: "h-8.5 px-350 text-body-sm font-semibold",
      md: "h-9.5 px-400 text-body font-bold",
    },
    selected: { true: "" },
  },
  compoundVariants: [{ selected: true, class: "bg-palette-bar text-palette-on-bar hover:text-palette-on-bar border-transparent hover:border-transparent hover:opacity-90" }],
  defaultVariants: { tone: "neutral", size: "sm" },
})

interface PillProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof pill> {
  selected?: boolean
  asChild?: boolean
}

/* `selected` left off means "not a toggle", so no aria-pressed — a Pill is also a link, a
   sheet trigger and a disclosure button, and those must not announce a pressed state. */
export const Pill = ({ tone, size, selected, type = "button", asChild = false, className, children, ...rest }: PillProps) => {
  const styles = pill({ tone, size, selected: selected ?? false, className })
  if (asChild) {
    return (
      <Slot className={styles} {...rest}>
        {children}
      </Slot>
    )
  }
  return (
    <button type={type} aria-pressed={selected} className={styles} {...rest}>
      {children}
    </button>
  )
}
